// Assets being edited in a form. An existing asset lives in the library store and saves half a second after the last
// change. A new one is a draft: it stays in memory, off the server and out of every list, until its first picture
// arrives (src/library/paintLook.ts), and then it is saved and moves into the library. A draft with no picture is thrown
// away when its form closes.
import { useSyncExternalStore } from "react";
import { api } from "../ui/api";
import type { Asset, AssetKind } from "../model/library";
import { findAsset } from "../model/library";
import { COLORS, blankAsset, hasPicture } from "./build";
import { dropAsset as removeFromLibrary, getLibrary, putAsset, useLibrary } from "./store";

let drafts: Record<string, Asset> = {};
let saveError: string | null = null;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach(l => l());
const subscribe = (l: () => void) => (listeners.add(l), () => listeners.delete(l));

export const getDraft = (id: string): Asset | undefined => drafts[id];
export function setDraft(a: Asset) { drafts = { ...drafts, [a.id]: a }; emit(); }
export function dropDraft(assetId: string) {
  if (!(assetId in drafts)) return;
  const { [assetId]: _gone, ...rest } = drafts;
  drafts = rest;
  emit();
}

// The newest copy of an asset, wherever it is.
export const latestAsset = (assetId: string): Asset | undefined => findAsset(getLibrary(), assetId) ?? drafts[assetId];

// A new draft of this kind; returns its id.
export function startDraft(kind: AssetKind): string {
  const a = blankAsset(kind, COLORS[getLibrary().assets.length % COLORS.length]!);
  setDraft(a);
  return a.id;
}

export function useAsset(assetId: string): Asset | undefined {
  const lib = useLibrary();
  const d = useSyncExternalStore(subscribe, () => drafts);
  return findAsset(lib, assetId) ?? d[assetId];
}
export const useSaveError = () => useSyncExternalStore(subscribe, () => saveError);
export function setSaveError(e: string | null) { saveError = e; emit(); }

const timers = new Map<string, ReturnType<typeof setTimeout>>();

// Saves the newest copy now: a new asset is created on the server (once it has a picture) and moves into the library.
export async function saveNow(assetId: string) {
  clearTimeout(timers.get(assetId));
  timers.delete(assetId);
  const a = latestAsset(assetId);
  if (!a || !hasPicture(a) || !a.name.trim()) return;
  const isDraft = !findAsset(getLibrary(), assetId);
  try {
    if (isDraft) {
      await api.createAsset(a);
      const now = latestAsset(assetId) ?? a;
      putAsset(now);
      dropDraft(assetId);
      if (now !== a) scheduleSave(assetId);
    } else await api.saveAsset(a);
    setSaveError(null);
  } catch (e) { setSaveError(`Couldn't save: ${(e as Error).message}`); }
}
export function scheduleSave(assetId: string) {
  clearTimeout(timers.get(assetId));
  timers.set(assetId, setTimeout(() => void saveNow(assetId), 500));
}

// Changes an asset (the newest copy, so a picture landing from a painting job is never overwritten by a stale one).
export function changeAsset(assetId: string, patch: Partial<Asset> | ((a: Asset) => Partial<Asset>)) {
  const a = latestAsset(assetId);
  if (!a) return;
  const next = { ...a, ...(typeof patch === "function" ? patch(a) : patch) };
  if (findAsset(getLibrary(), assetId)) putAsset(next); else setDraft(next);
  scheduleSave(assetId);
}

// The library assets a picture was just painted from are marked as used, so they sort to the front of the pickers.
export function markUsed(refs: { src: string; kind: string }[]) {
  const at = Date.now();
  for (const a of getLibrary().assets) if (refs.some(r => r.kind === a.kind && r.src === a.picture.src)) changeAsset(a.id, { usedAt: at });
}

// After an asset's form closes: a picked reference to it takes its current picture and name (both can have changed).
// `was`: the reference as it was picked.
export function refreshRef<R extends { src: string; name: string; kind: string }>(refs: R[], was: R, assetId: string): R[] {
  const a = findAsset(getLibrary(), assetId);
  if (!a?.picture.src) return refs.filter(r => !(r.src === was.src && r.kind === was.kind));
  return refs.map(r => (r.src === was.src && r.kind === was.kind ? { ...r, src: a.picture.src, name: a.name } : r));
}

export async function deleteAsset(assetId: string) {
  clearTimeout(timers.get(assetId));
  if (findAsset(getLibrary(), assetId)) {
    await api.deleteAsset(assetId);
    removeFromLibrary(assetId);
  }
  dropDraft(assetId);
}
